/**
 * The pure logic this feature's components branch on: which tabs the server
 * actually offers (filtered to the keys this client understands, `## 5.7`
 * of ISSUE-180's plan; ISSUE-987 retired `teams` for `lineup`), which one is
 * active, whether this event's layout is the not-yet-built `Coming soon`
 * state, and how Plays orders its entries.
 *
 * Plays arrive already grouped into periods by `/event/plays-get`
 * (ISSUE-986), so there is no grouping function here; this file only orders
 * them and picks the poll interval.
 */

export type typeGameDetailTabKey =
  "matchup" | "summary" | "plays" | "injuries" | "odds" | "boxScore" | "lineup";

/** Every tab key this client renders, in the order the strip shows them
 *  (ISSUE-1086, owner 2026-10-08): Matchup, Rosters (key `lineup`), Injuries,
 *  Odds, then Box score and Plays. */
const KNOWN_TAB_KEYS: readonly typeGameDetailTabKey[] = [
  "matchup",
  "summary",
  "lineup",
  "injuries",
  "odds",
  "boxScore",
  "plays",
];

export const DEFAULT_TAB_KEY: typeGameDetailTabKey = "matchup";

/**
 * Tabs actually offered, filtered to the keys this client understands and
 * put in KNOWN_TAB_KEYS order, whatever order the server sent. Labels come
 * from the server ("Rosters" for `lineup`).
 */
export function resolveOfferedTabs(
  serverTabs: readonly { key: string; label: string }[],
): { key: typeGameDetailTabKey; label: string }[] {
  const known = serverTabs.filter(
    (t): t is { key: typeGameDetailTabKey; label: string } =>
      (KNOWN_TAB_KEYS as readonly string[]).includes(t.key),
  );
  return known
    .map((tab, index) => ({ tab, index }))
    .sort((a, b) => {
      const rankDiff =
        KNOWN_TAB_KEYS.indexOf(a.tab.key) - KNOWN_TAB_KEYS.indexOf(b.tab.key);
      return rankDiff !== 0 ? rankDiff : a.index - b.index;
    })
    .map(({ tab }) => tab);
}

/**
 * The active tab: the requested value when it is actually offered, else the
 * first offered tab, else the default. Never a value the fan cannot
 * actually see a strip item for.
 */
export function resolveActiveTabKey(
  offeredTabs: readonly { key: typeGameDetailTabKey }[],
  requestedTab: string | undefined,
): typeGameDetailTabKey {
  const offeredKeys = offeredTabs.map((t) => t.key);
  if (
    requestedTab !== undefined &&
    (offeredKeys as string[]).includes(requestedTab)
  ) {
    return requestedTab as typeGameDetailTabKey;
  }
  return offeredTabs[0]?.key ?? DEFAULT_TAB_KEY;
}

/**
 * SCORES-3/Q61: golf and MMA ship Coming soon at launch -- everything that
 * is not the two-team layout. Covers today's only non-team value
 * ("leaderboard") and any future one, without a schema change.
 */
export function isComingSoon(
  layout: "twoTeam" | "leaderboard" | (string & {}),
): boolean {
  return layout !== "twoTeam";
}

/**
 * ISSUE-167 R4: the server always returns oldest-first. Newest-first while
 * live is a display-only reversal, never re-requested from the server.
 */
export function orderPlaysForDisplay<T>(
  entries: readonly T[],
  isLive: boolean,
): T[] {
  return isLive ? [...entries].reverse() : [...entries];
}

/**
 * ISSUE-986 R4. How often the Plays tab refetches: the server's own
 * `refreshAfterSeconds` while the game is live, with slow idle/recovery
 * checks for scheduled games and later score corrections.
 */
export function playsRefetchIntervalMs({
  isLive,
  refreshAfterSeconds,
}: {
  isLive: boolean;
  refreshAfterSeconds: number | null | undefined;
}): number | false {
  if (!isLive) return 120_000;
  if (typeof refreshAfterSeconds !== "number" || refreshAfterSeconds <= 0) {
    return 120_000;
  }
  return refreshAfterSeconds * 1000;
}

/** The scroll distance, in CSS pixels, past which the header collapses to
 *  its compact bar. */
export const HEADER_COLLAPSE_THRESHOLD_PX = 80;

/** Every `/scores` pane returns to the Scores section it was opened from. */
export const GAME_DETAIL_BACK_LABEL = "Back to Scores";

/** The red pill under a live game's period and clock (spec 7.1). The server
 *  sends no separate period label, so the pill reads this fallback. */
export const GAME_DETAIL_LIVE_PILL_LABEL = "Live";

/** The game screen's own title, "KC at BUF" (spec 7.1): the away team at the
 *  home team, by the server's abbreviations. */
export function gameDetailTitle(input: {
  awayAbbreviation: string;
  homeAbbreviation: string;
}): string {
  return `${input.awayAbbreviation} at ${input.homeAbbreviation}`;
}

/** Title of the state shown when the game does not exist. */
export const GAME_DETAIL_MISSING_TITLE = "Game not found";

/** Body line under `GAME_DETAIL_MISSING_TITLE`. */
export const GAME_DETAIL_MISSING_BODY =
  "The link may be out of date, or the game was removed.";

/** Line shown when the game could not be loaded and a retry may help. */
export const GAME_DETAIL_FAILED_LINE = "We could not load this game.";

/** Label of the button that reruns the game read. */
export const GAME_DETAIL_RETRY_LABEL = "Try again";

/** Label the retry button announces while the read is running again. */
export const GAME_DETAIL_RETRY_LOADING_LABEL = "Loading the game";

/** Which state a settled game read with no game shows. */
export type typeGameDetailFailureKind = "missing" | "failed";

/** Codes whose answer a retry can never change, so the screen offers no Try again. */
export const GAME_DETAIL_MISSING_CODES: readonly string[] = [
  "NOT_FOUND",
  "VALIDATION_ERROR",
];

/**
 * Decides which state a game read that settled with no game shows.
 * NOT_FOUND is a game that does not exist. VALIDATION_ERROR on this read is a
 * malformed eventId or zone, and retrying returns the same answer. Every other
 * code, and a read with no code at all, is a failure a retry can fix.
 */
export function resolveGameDetailFailureKind(
  errorCode: string | null,
): typeGameDetailFailureKind {
  return errorCode !== null && GAME_DETAIL_MISSING_CODES.includes(errorCode)
    ? "missing"
    : "failed";
}
